import { CLINIC_ID, supabase } from "./supabase.js";
import { jakartaDateKey, jakartaDayBounds } from "./lib/format.js";
import { canonicalDivision } from "./lib/division.js";

function ensureClient() {
  if (!supabase) throw new Error("Supabase belum dikonfigurasi.");
  return supabase;
}

function assertResult(result) {
  if (result.error) throw result.error;
  return result.data;
}

async function clientMap(ids) {
  const unique = [...new Set((ids || []).filter(Boolean))];
  if (!unique.length) return {};
  const result = await ensureClient()
    .from("clients")
    .select("id,client_code,name,phone,birth_date,gender")
    .eq("clinic_id", CLINIC_ID)
    .in("id", unique);
  const rows = assertResult(result) || [];
  return Object.fromEntries(rows.map((row) => [row.id, row]));
}

function attachClients(rows, map) {
  return (rows || []).map((row) => ({
    ...row,
    client: map[row.client_id] || null,
  }));
}

async function visitMap(ids) {
  const unique = [...new Set((ids || []).filter(Boolean))];
  if (!unique.length) return {};
  const result = await ensureClient()
    .from("visits")
    .select("id,visit_number,room_name,visit_status")
    .eq("clinic_id", CLINIC_ID)
    .in("id", unique);
  const rows = assertResult(result) || [];
  return Object.fromEntries(rows.map((row) => [row.id, row]));
}

function attachVisits(rows, map) {
  return (rows || []).map((row) => ({
    ...row,
    visit: map[row.visit_id] || null,
  }));
}

export async function resolveIdentity(authUser) {
  const db = ensureClient();
  const appUserResult = await db
    .from("users")
    .select("id,name,username,status,is_platform_admin")
    .eq("auth_user_id", authUser.id)
    .maybeSingle();
  const appUser = assertResult(appUserResult);
  if (!appUser || appUser.status !== "ACTIVE") {
    throw new Error(
      "Akun Auth belum dipetakan ke pengguna aplikasi yang aktif.",
    );
  }

  const membershipResult = await db
    .from("clinic_memberships")
    .select("clinic_id,user_id,role_id,status")
    .eq("clinic_id", CLINIC_ID)
    .eq("user_id", appUser.id)
    .maybeSingle();
  const membership = assertResult(membershipResult);
  if (!membership || membership.status !== "ACTIVE") {
    throw new Error(
      `Akun ini tidak mempunyai akses aktif ke klinik ${CLINIC_ID}.`,
    );
  }

  const [roleResult, permissionsResult, clinicResult, staffResult] = await Promise.all([
    db
      .from("roles")
      .select(
        "id,role_name,menu_access,can_see_revenue_today,can_see_revenue_month",
      )
      .eq("id", membership.role_id)
      .maybeSingle(),
    db
      .from("role_permissions")
      .select("permission_code")
      .eq("role_id", membership.role_id),
    db
      .from("clinics")
      .select("code,name,domain,timezone,status")
      .eq("code", CLINIC_ID)
      .maybeSingle(),
    db
      .from("staff")
      .select("id,name,division,allowance_per_attendance,payroll_type")
      .eq("clinic_id", CLINIC_ID)
      .eq("user_id", appUser.id)
      .maybeSingle(),
  ]);

  const role = assertResult(roleResult);
  const permissions = assertResult(permissionsResult) || [];
  const clinic = assertResult(clinicResult);
  if (!role || !clinic || clinic.status !== "ACTIVE")
    throw new Error("Konfigurasi klinik atau role tidak aktif.");

  // Load active modules (silently — fallback ke semua aktif jika tabel belum ada)
  let activeModules = {};
  try {
    activeModules = await loadClinicModules();
    // Jika belum ada data sama sekali, default semua aktif (Wellness + Estetik)
    if (Object.keys(activeModules).length === 0) {
      activeModules = { wellness: true, estetik: true };
    }
  } catch {
    activeModules = { wellness: true, estetik: true };
  }

  return {
    authUser,
    appUser,
    membership,
    role,
    clinic,
    permissions: new Set(permissions.map((row) => row.permission_code)),
    staffMember: staffResult.data || null,
    activeModules,
  };
}

export async function inviteAppUserAccess({ appUserId, email }) {
  const { data, error } = await ensureClient().functions.invoke("admin-invite-user", {
    body: {
      clinic_id: CLINIC_ID,
      app_user_id: appUserId,
      email,
    },
  });
  if (error) throw error;
  return data;
}

export async function recordCheckIn(staffMember) {
  const db = ensureClient();
  const today = jakartaDateKey();
  const attId = `ATT-${staffMember.id}-${today.replace(/-/g, "")}`;
  const { data: existing } = await db
    .from("attendance")
    .select("id,check_in_at")
    .eq("id", attId)
    .maybeSingle();
  if (existing) return; // sudah check-in hari ini
  await db.from("attendance").insert({
    id:                   attId,
    clinic_id:            CLINIC_ID,
    nakes_id:             staffMember.id,
    attendance_date:      today,
    status:               "Hadir",
    allowance_amount:     staffMember.allowance_per_attendance || 0,
    check_in_at:          new Date().toISOString(),
    notes:                "Login otomatis",
  });
}

export async function recordCheckOut(staffMember) {
  const db = ensureClient();
  const today = jakartaDateKey();
  const attId = `ATT-${staffMember.id}-${today.replace(/-/g, "")}`;
  await db.from("attendance").update({
    check_out_at: new Date().toISOString(),
    status:       "Pulang",
    notes:        "Logout otomatis",
  }).eq("id", attId);
}

export async function loadDashboard(identity) {
  const db = ensureClient();
  const today = jakartaDateKey();
  const [start, end] = jakartaDayBounds();
  const showRevenue = identity.role.can_see_revenue_today;

  const [
    clientsResult,
    visitsResult,
    transactionsResult,
    notesResult,
    lowStockResult,
  ] = await Promise.all([
    db
      .from("clients")
      .select("id", { count: "exact", head: true })
      .eq("clinic_id", CLINIC_ID),
    db
      .from("visits")
      .select(
        "id,visit_number,client_id,visit_date,doctor_name,room_name,visit_status,payment_status,division,created_at",
      )
      .eq("clinic_id", CLINIC_ID)
      .in("visit_status", ["TERDAFTAR","PEMERIKSAAN","TINDAKAN","READY_PAYMENT","SIAP_BAYAR"])
      .order("created_at", { ascending: false })
      .limit(50),
    db
      .from("transactions")
      .select(
        "id,invoice_number,client_id,transaction_date,net_amount,amount_received,payment_status,payment_method",
      )
      .eq("clinic_id", CLINIC_ID)
      .gte("transaction_date", start)
      .lt("transaction_date", end)
      .order("transaction_date", { ascending: false })
      .limit(100),
    db
      .from("medical_notes")
      .select(
        "id,visit_id,client_id,doctor_name,current_complaint,syndrome_status,draft_status,created_at",
      )
      .eq("clinic_id", CLINIC_ID)
      .gte("created_at", start)
      .lt("created_at", end)
      .order("created_at", { ascending: false })
      .limit(100),
    identity.permissions.has("inventory.read")
      ? db
          .from("bhp_items")
          .select("id,item_code,name,current_stock,minimum_stock,unit,status")
          .eq("clinic_id", CLINIC_ID)
          .eq("status", "ACTIVE")
          .order("name")
          .limit(100)
      : Promise.resolve({ data: [], error: null }),
  ]);

  for (const result of [
    clientsResult,
    visitsResult,
    transactionsResult,
    notesResult,
    lowStockResult,
  ]) {
    if (result.error) throw result.error;
  }

  const visits = visitsResult.data || [];
  const transactions = transactionsResult.data || [];
  const notes = notesResult.data || [];
  const map = await clientMap([
    ...visits.map((row) => row.client_id),
    ...transactions.map((row) => row.client_id),
    ...notes.map((row) => row.client_id),
  ]);
  const lowStock = (lowStockResult.data || []).filter(
    (row) => Number(row.current_stock) <= Number(row.minimum_stock),
  );

  return {
    today,
    clientCount: clientsResult.count || 0,
    visits: attachClients(visits, map),
    transactions: attachClients(transactions, map),
    notes: attachClients(notes, map),
    lowStock,
    waitingPayment: visits.filter(
      (row) => /SIAP_BAYAR|READY/i.test(row.payment_status || ""),
    ).length,
    revenue: showRevenue
      ? transactions.reduce(
          (sum, row) => sum + Number(row.amount_received || 0),
          0,
        )
      : null,
  };
}

export async function loadPageData(page, search = "", identity = null) {
  const db = ensureClient();
  const term = search.trim();

  if (page === "clients") {
    if (term.length < 2) return [];
    let query = db
      .from("clients")
      .select(
        "id,client_code,name,birth_date,gender,phone,address,notes,client_category,employee_code,employment_unit,corporate_id,pricing_policy_id,nik,referred_by_code,satusehat_consent,satusehat_patient_id,satusehat_patient_status,satusehat_patient_checked_at,satusehat_patient_error,updated_at",
      )
      .eq("clinic_id", CLINIC_ID)
      .order("name")
      .limit(150);
    if (term)
      query = query.or(
        `name.ilike.%${term}%,client_code.ilike.%${term}%,phone.ilike.%${term}%`,
      );
    return assertResult(await query) || [];
  }

  if (page === "registration" || page === "nakesboard") {
    let query = db
      .from("visits")
      .select(
        "id,visit_number,client_id,visit_date,doctor_name,room_name,visit_status,payment_status,division,created_at",
      )
      .eq("clinic_id", CLINIC_ID)
      .order("visit_date", { ascending: false })
    .order("created_at", { ascending: false })
      .limit(150);
    if (page === "nakesboard") query = query.in("visit_status", ["TINDAKAN", "TERDAFTAR", "PEMERIKSAAN"]);
    if (page === "nakesboard" && identity) {
      const roleName = identity.role?.role_name || "";
      const unrestricted = /^(admin|kasir|frontliner)$/i.test(roleName);
      const division = canonicalDivision(identity.staffMember?.division);
      if (!unrestricted && division && division !== "ALL") {
        query = query.eq("division", division);
      } else if (!unrestricted && !division) {
        query = query.eq("id", "__no_access__");
      }
    }
    const rows = assertResult(await query) || [];
    let noteStatusMap = {};
    let consentMap = {};
    if (page === "nakesboard" && rows.length) {
      const visitIds = rows.map((row) => row.id);
      const noteRequests = [
        db.from("medical_notes").select("visit_id,draft_status").eq("clinic_id", CLINIC_ID).in("visit_id", visitIds),
        db.from("estetik_notes").select("visit_id,draft_status").eq("clinic_id", CLINIC_ID).in("visit_id", visitIds),
        db.from("dental_notes").select("visit_id,draft_status").eq("clinic_id", CLINIC_ID).in("visit_id", visitIds),
        db.from("dokter_umum_notes").select("visit_id,draft_status").eq("clinic_id", CLINIC_ID).in("visit_id", visitIds),
      ];
      const noteResults = await Promise.allSettled(noteRequests);
      const notes = noteResults.flatMap((result) => {
        if (result.status !== "fulfilled" || result.value.error) return [];
        return result.value.data || [];
      });
      noteStatusMap = Object.fromEntries(
        notes.map((note) => [note.visit_id, note.draft_status]),
      );
      const consentResult = await db
        .from("informed_consents")
        .select("visit_id,storage_url,signed_at")
        .eq("clinic_id", CLINIC_ID)
        .in("visit_id", visitIds);
      const consents = assertResult(consentResult) || [];
      consentMap = Object.fromEntries(
        consents.map((c) => [c.visit_id, c.storage_url ? "SIGNED" : "DRAFT"]),
      );
    }
    const map = await clientMap(rows.map((row) => row.client_id));
    const enriched = attachClients(rows, map).map((row) => ({
      ...row,
      medical_note_status: noteStatusMap[row.id] || "",
      consent_status: consentMap[row.id] || null,
    }));
    if (!term) return enriched;
    const lowered = term.toLowerCase();
    return enriched.filter((row) =>
      [row.visit_number, row.client?.name, row.doctor_name, row.visit_status]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(lowered)),
    );
  }

  if (page === "payments") {
    let query = db
      .from("transactions")
      .select(
        "id,invoice_number,client_id,transaction_date,total_amount,discount_amount,net_amount,amount_received,payment_status,payment_method,visit_id",
      )
      .eq("clinic_id", CLINIC_ID)
      .order("transaction_date", { ascending: false })
      .limit(150);
    if (term) query = query.ilike("invoice_number", `%${term}%`);
    const rows = assertResult(await query) || [];
    const vMap = await visitMap(rows.map((row) => row.visit_id));
    return attachVisits(
      attachClients(rows, await clientMap(rows.map((row) => row.client_id))),
      vMap,
    );
  }

  if (page === "packages") {
    const rows =
      assertResult(
        await db.rpc("package_page_data", { p_clinic_id: CLINIC_ID }),
      ) || [];
    if (!term) return rows;
    const lowered = term.toLowerCase();
    return rows.filter((row) =>
      [
        row.client?.name,
        row.client?.client_code,
        row.package?.package_name,
        row.invoice_number,
        row.status,
        row.payment_status,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(lowered)),
    );
  }

  if (page === "medicalRecords") {
    let query = db
      .from("medical_notes")
      .select(
        "id,visit_id,client_id,doctor_name,blood_pressure,bpm,current_complaint,tcm_pulse,tcm_tongue,syndrome_status,draft_status,created_at",
      )
      .eq("clinic_id", CLINIC_ID)
      .order("created_at", { ascending: false })
      .limit(150);
    if (term) query = query.ilike("current_complaint", `%${term}%`);
    const rows = assertResult(await query) || [];
    return attachClients(
      rows,
      await clientMap(rows.map((row) => row.client_id)),
    );
  }

  if (page === "bhp") {
    let query = db
      .from("bhp_items")
      .select(
        "id,item_code,name,category,unit,current_stock,minimum_stock,target_stock,average_cost,division,status,updated_at",
      )
      .eq("clinic_id", CLINIC_ID)
      .order("name")
      .limit(150);
    if (term)
      query = query.or(
        `name.ilike.%${term}%,item_code.ilike.%${term}%,category.ilike.%${term}%`,
      );
    return assertResult(await query) || [];
  }

  return [];
}

export async function loadDailyClosingData(closingDate) {
  return assertResult(
    await ensureClient().rpc("daily_closing_data", {
      p_clinic_id: CLINIC_ID,
      p_closing_date: closingDate,
    }),
  );
}

export async function finalizeDailyClosing(closingDate, payload) {
  return assertResult(
    await ensureClient().rpc("finalize_daily_closing", {
      p_clinic_id: CLINIC_ID,
      p_closing_date: closingDate,
      p_payload: payload,
    }),
  );
}

export async function loadProcurementData() {
  return (
    assertResult(
      await ensureClient().rpc("procurement_page_data_v084", {
        p_clinic_id: CLINIC_ID,
      }),
    ) || {
      suppliers: [],
      items: [],
      purchase_orders: [],
      goods_receipts: [],
      supplier_returns: [],
      supplier_invoices: [],
      supplier_refunds: [],
    }
  );
}

export async function loadProcurementPurchaseOrderPrint(purchaseOrderId) {
  const db = ensureClient();
  const [poResult, clinicResult] = await Promise.all([
    db.rpc("procurement_purchase_order_print_data_v082", {
      p_clinic_id: CLINIC_ID,
      p_po_id: purchaseOrderId,
    }),
    db.from("clinics")
      .select("code,name,legal_name,timezone,contact")
      .eq("code", CLINIC_ID)
      .maybeSingle(),
  ]);
  const poData   = assertResult(poResult);
  const fullClinic = clinicResult.data || {};
  // Merge fresh clinic data (including contact) over whatever the RPC returned
  if (poData && fullClinic.code) {
    poData.clinic = { ...(poData.clinic || {}), ...fullClinic };
  }
  return poData;
}

export async function saveProcurementSupplier(supplier) {
  return assertResult(
    await ensureClient().rpc("save_procurement_supplier", {
      p_clinic_id: CLINIC_ID,
      p_supplier: supplier,
    }),
  );
}

export async function saveProcurementPurchaseOrder(
  order,
  items,
  submit = false,
) {
  return assertResult(
    await ensureClient().rpc("save_procurement_purchase_order_v082", {
      p_clinic_id: CLINIC_ID,
      p_order: order,
      p_items: items,
      p_submit: submit,
    }),
  );
}

export async function postProcurementGoodsReceipt(receipt, items) {
  return assertResult(
    await ensureClient().rpc("post_procurement_goods_receipt_v082", {
      p_clinic_id: CLINIC_ID,
      p_receipt: receipt,
      p_items: items,
    }),
  );
}

export async function reviewProcurementPurchaseOrder(
  purchaseOrderId,
  action,
  reason = "",
) {
  return assertResult(
    await ensureClient().rpc("review_procurement_purchase_order", {
      p_clinic_id: CLINIC_ID,
      p_po_id: purchaseOrderId,
      p_action: action,
      p_reason: reason,
    }),
  );
}

export async function markProcurementPurchaseOrderOrdered(purchaseOrderId) {
  return assertResult(
    await ensureClient().rpc("mark_procurement_purchase_order_ordered", {
      p_clinic_id: CLINIC_ID,
      p_po_id: purchaseOrderId,
    }),
  );
}

export async function recordProcurementSupplierInvoice(receiptId, invoice) {
  return assertResult(
    await ensureClient().rpc("record_procurement_supplier_invoice", {
      p_clinic_id: CLINIC_ID,
      p_receipt_id: receiptId,
      p_invoice: invoice,
    }),
  );
}

export async function payProcurementSupplierInvoice(invoiceId, payment) {
  return assertResult(
    await ensureClient().rpc("pay_procurement_supplier_invoice", {
      p_clinic_id: CLINIC_ID,
      p_invoice_id: invoiceId,
      p_payment: payment,
    }),
  );
}

export async function postProcurementSupplierReturn(supplierReturn, items) {
  return assertResult(
    await ensureClient().rpc("post_procurement_supplier_return_v084", {
      p_clinic_id: CLINIC_ID,
      p_return: supplierReturn,
      p_items: items,
    }),
  );
}

export async function receiveProcurementSupplierRefund(
  supplierReturnId,
  refund,
) {
  return assertResult(
    await ensureClient().rpc("receive_procurement_supplier_refund_v084", {
      p_clinic_id: CLINIC_ID,
      p_return_id: supplierReturnId,
      p_refund: refund,
    }),
  );
}

export async function loadRegistrationFormData() {
  const db = ensureClient();
  const [referencesResult, staffResult] = await Promise.all([
    db.rpc("registration_reference_data", { p_clinic_id: CLINIC_ID }),
    db
      .from("staff")
      .select("id,name,division")
      .eq("clinic_id", CLINIC_ID)
      .eq("status", "ACTIVE"),
  ]);
  const references = assertResult(referencesResult) || {};
  const staffFull  = assertResult(staffResult)      || [];
  // Merge division ke staff dari RPC
  const divMap = Object.fromEntries(staffFull.map(s => [s.name, s.division]));
  const staff  = (references.staff || []).map(s => ({
    ...s,
    division: divMap[s.name] || "ALL",
  }));
  let marketingUsers = [];
  try {
    const [usersResult, membershipsResult, rolesResult] = await Promise.all([
      db.from('users').select('id,name,username,legacy_role,status').eq('status', 'ACTIVE').order('name'),
      db.from('clinic_memberships').select('user_id,role_id').eq('clinic_id', CLINIC_ID).eq('status', 'ACTIVE'),
      db.from('roles').select('id,role_name'),
    ]);
    if (!usersResult.error && !membershipsResult.error && !rolesResult.error) {
      const roleMap = Object.fromEntries((rolesResult.data || []).map((row) => [row.id, row.role_name]));
      const activeUserIds = new Set((membershipsResult.data || []).map((row) => row.user_id));
      marketingUsers = (usersResult.data || [])
        .filter((user) => activeUserIds.has(user.id))
        .filter((user) => /(front|marketing)/i.test(`${user.legacy_role || ''} ${roleMap[(membershipsResult.data || []).find((row) => row.user_id === user.id)?.role_id] || ''}`))
        .map(({ id, name, username }) => ({ id, name, username }))
        .sort((a, b) => String(a.name || a.username).localeCompare(String(b.name || b.username)));
    }
  } catch {
    // Fallback ke input teks jika RLS belum membuka referensi users.
  }
  return {
    staff,
    corporates:     references.corporates      || [],
    pricingPolicies: references.pricing_policies || [],
    marketingUsers,
  };
}

export async function loadMarketingReferralForClient(clientId) {
  if (!clientId) return null;
  const result = await ensureClient()
    .from('marketing_referral_links')
    .select('id,marketer_user_id,status,linked_at')
    .eq('clinic_id', CLINIC_ID)
    .eq('referred_client_id', clientId)
    .eq('status', 'ACTIVE')
    .maybeSingle();
  return assertResult(result);
}

export async function searchRegistrationClients(query, limit = 20) {
  const term = String(query || '').trim();
  if (term.length < 2) return [];
  const safeTerm = term.replace(/[%_,]/g, (char) => `\\${char}`);
  const result = await ensureClient()
    .from('clients')
    .select('id,client_code,name,birth_date,gender,phone,address,notes,client_category,employee_code,employment_unit,corporate_id,pricing_policy_id,nik,referred_by_code,satusehat_consent,satusehat_patient_id,satusehat_patient_status,satusehat_patient_checked_at,satusehat_patient_error')
    .eq('clinic_id', CLINIC_ID)
    .or(`name.ilike.%${safeTerm}%,client_code.ilike.%${safeTerm}%,phone.ilike.%${safeTerm}%`)
    .order('name')
    .limit(limit);
  return assertResult(result) || [];
}

export async function saveRegistrationClient(client) {
  const result = await ensureClient().rpc("save_registration_client", {
    p_clinic_id: CLINIC_ID,
    p_client: client,
  });
  return assertResult(result);
}

export async function createMgmMember(clientId) {
  const result = await ensureClient().rpc('create_mgm_member', {
    p_clinic_id: CLINIC_ID,
    p_client_id: clientId,
  });
  return assertResult(result);
}

export async function linkMgmReferral(referralCode, referredClientId) {
  const result = await ensureClient().rpc('link_mgm_referral', {
    p_clinic_id: CLINIC_ID,
    p_referral_code: String(referralCode || '').replace(/^MGM:/i, '').trim(),
    p_referred_client_id: referredClientId,
  });
  return assertResult(result);
}

export async function linkMgmMarketingReferral(marketerId, referredClientId) {
  const result = await ensureClient().rpc('link_mgm_marketing_referral', {
    p_clinic_id: CLINIC_ID,
    p_marketer_id: String(marketerId || '').trim(),
    p_referred_client_id: referredClientId,
  });
  return assertResult(result);
}

export async function setMgmMarketingReferral(marketerId, referredClientId) {
  const result = await ensureClient().rpc('set_client_marketing_referrer', {
    p_clinic_id: CLINIC_ID,
    p_marketer_id: String(marketerId || '').trim() || null,
    p_referred_client_id: referredClientId,
  });
  return assertResult(result);
}

export async function loadMgmMembers() {
  const db = ensureClient();
  const result = await db.from('mgm_members')
    .select('id,clinic_id,client_id,referral_code,status,commission_rate,created_at')
    .eq('clinic_id', CLINIC_ID)
    .order('created_at', { ascending: false });
  const rows = assertResult(result) || [];
  const ids = [...new Set(rows.map((row) => row.client_id).filter(Boolean))];
  if (!ids.length) return rows;
  const clients = await db.from('clients').select('id,name,client_code,phone').in('id', ids);
  const map = new Map((assertResult(clients) || []).map((client) => [client.id, client]));
  return rows.map((row) => ({ ...row, client: map.get(row.client_id) || null }));
}

export async function loadMgmLedger() {
  const db = ensureClient();
  const result = await db.from('mgm_transaction_ledger')
    .select('id,transaction_id,visit_id,member_id,referrer_client_id,referrer_user_id,referrer_type,referred_client_id,transaction_date,eligible_amount,commission_rate,commission_amount,eligibility_status,void_reason,created_at')
    .eq('clinic_id', CLINIC_ID)
    .order('transaction_date', { ascending: false })
    .limit(200);
  const rows = assertResult(result) || [];
  const clientIds = [...new Set(rows.flatMap((row) => [row.referrer_client_id, row.referred_client_id]).filter(Boolean))];
  const userIds = [...new Set(rows.map((row) => row.referrer_user_id).filter(Boolean))];
  const [clients, users] = await Promise.all([
    clientIds.length ? db.from('clients').select('id,name,client_code').in('id', clientIds) : Promise.resolve({ data: [], error: null }),
    userIds.length ? db.from('users').select('id,name,username').in('id', userIds) : Promise.resolve({ data: [], error: null }),
  ]);
  const clientMap = new Map((assertResult(clients) || []).map((client) => [client.id, client]));
  const userMap = new Map((assertResult(users) || []).map((user) => [user.id, user]));
  return rows.map((row) => ({
    ...row,
    referrer: userMap.get(row.referrer_user_id) || clientMap.get(row.referrer_client_id) || null,
    referred: clientMap.get(row.referred_client_id) || null,
  }));
}

export async function loadMgmClaims() {
  const db = ensureClient();
  const result = await db.from('mgm_claims')
    .select('id,member_id,member_client_id,claim_type,amount,status,bank_name,account_name,account_number,settlement_reference,notes,requested_at,approved_at,settled_at')
    .eq('clinic_id', CLINIC_ID)
    .order('requested_at', { ascending: false })
    .limit(200);
  const rows = assertResult(result) || [];
  const clientIds = [...new Set(rows.map((row) => row.member_client_id).filter(Boolean))];
  const clients = clientIds.length
    ? await db.from('clients').select('id,name,client_code').in('id', clientIds)
    : { data: [], error: null };
  const clientMap = new Map((assertResult(clients) || []).map((client) => [client.id, client]));
  return rows.map((row) => ({ ...row, member_client: clientMap.get(row.member_client_id) || null }));
}

export async function requestMgmClaim(payload) {
  const result = await ensureClient().rpc('request_mgm_claim', {
    p_clinic_id: CLINIC_ID,
    p_member_id: payload.member_id,
    p_claim_type: payload.claim_type,
    p_bank_name: payload.bank_name || null,
    p_account_name: payload.account_name || null,
    p_account_number: payload.account_number || null,
    p_notes: payload.notes || null,
  });
  return assertResult(result);
}

export async function settleMgmClaim(claimId, status, settlementReference = null) {
  const result = await ensureClient().rpc('settle_mgm_claim', {
    p_clinic_id: CLINIC_ID,
    p_claim_id: claimId,
    p_status: status,
    p_settlement_reference: settlementReference,
  });
  return assertResult(result);
}

export async function loadMarketingCommissionLedger(dateFrom, dateTo) {
  let query = ensureClient().from('marketing_commission_ledger')
    .select('id,marketer_user_id,referred_client_id,transaction_id,transaction_date,eligible_amount,commission_rate,commission_amount,eligibility_status')
    .eq('clinic_id', CLINIC_ID)
    .neq('eligibility_status', 'VOID');
  if (dateFrom) query = query.gte('transaction_date', dateFrom);
  if (dateTo) query = query.lt('transaction_date', dateTo);
  const result = await query.order('transaction_date', { ascending: false });
  return assertResult(result) || [];
}

export async function lookupSatusehatPatientForClient(client) {
  const nik = String(client?.nik || "").trim();
  if (!client?.id) throw new Error("ID pasien tidak tersedia.");
  if (!client?.satusehat_consent) throw new Error("Consent SatuSehat belum dicentang.");
  if (!/^\d{16}$/.test(nik)) throw new Error("NIK harus 16 digit angka.");

  const db = ensureClient();
  const result = await db.functions.invoke("satusehat-patient-lookup", {
    body: { nik, client_id: client.id, clinic_id: CLINIC_ID },
  });
  if (result.error) {
    let message = result.error.message || String(result.error);
    try {
      const context = await result.error.context?.json?.();
      if (context?.error) message = context.error;
    } catch {
      // Supabase function errors do not always include a readable JSON body.
    }
    throw new Error(`SATUSEHAT lookup gagal: ${message}`);
  }

  const lookup = result.data || {};
  const updatePayload = {
    satusehat_patient_id: lookup.satusehat_patient_id || null,
    satusehat_patient_status: lookup.status || "ERROR",
    satusehat_patient_checked_at: lookup.checked_at || new Date().toISOString(),
    satusehat_patient_error: lookup.error || null,
  };

  const updateResult = await db
    .from("clients")
    .update(updatePayload)
    .eq("clinic_id", CLINIC_ID)
    .eq("id", client.id)
    .select(
      "id,client_code,name,birth_date,gender,phone,address,notes,client_category,employee_code,employment_unit,corporate_id,pricing_policy_id,nik,referred_by_code,satusehat_consent,satusehat_patient_id,satusehat_patient_status,satusehat_patient_checked_at,satusehat_patient_error,updated_at",
    )
    .single();

  return assertResult(updateResult);
}

export async function registerClinicVisit(payload) {
  const db = ensureClient();
  const result = await db.rpc("register_clinic_visit", {
    p_clinic_id: CLINIC_ID,
    p_client_id: payload.client_id,
    p_visit_date: payload.visit_date,
    p_doctor_name: payload.doctor_name,
    p_room_name: payload.room_name || null,
    p_division: payload.division || 'WELLNESS',
  });
  const visit = assertResult(result);

  // Auto-resolve FU: tandai kunjungan lama klien ini sebagai "FU selesai"
  if (payload.client_id) {
    const newVisitId = visit?.visit_id || visit?.id || null;
    let q = db.from("visits")
      .update({ fu_resolved_at: new Date().toISOString() })
      .eq("clinic_id", CLINIC_ID)
      .eq("client_id", payload.client_id)
      .is("fu_resolved_at", null);
    if (newVisitId) q = q.neq("id", newVisitId);
    try {
      await q;
    } catch {
      // Tidak memblokir registrasi bila auto-resolve FU gagal.
    }
  }

  return visit;
}

export async function createPatientPortalToken(clientId, ttlHours = 720) {
  const result = await ensureClient().rpc("patient_create_access_token", {
    p_clinic_id: CLINIC_ID,
    p_client_id: clientId,
    p_ttl_hours: ttlHours,
  });
  return assertResult(result);
}

export async function patientPortalLogin({ client_code, phone, token }) {
  const result = await ensureClient().rpc("patient_portal_login", {
    p_clinic_id: CLINIC_ID,
    p_client_code: client_code,
    p_phone: phone,
    p_token: token,
  });
  return assertResult(result);
}

export async function loadPatientPortalData(sessionToken) {
  const result = await ensureClient().rpc("patient_portal_data", {
    p_session_token: sessionToken,
  });
  return assertResult(result);
}

export async function loadPatientPortalReservationOptions(sessionToken) {
  const result = await ensureClient().rpc("patient_portal_reservation_options", {
    p_session_token: sessionToken,
  });
  return assertResult(result);
}

export async function createPatientPortalAppointment(sessionToken, appointment) {
  const result = await ensureClient().rpc("patient_portal_create_appointment", {
    p_session_token: sessionToken,
    p_division: appointment.division,
    p_nakes_id: appointment.nakes_id || null,
    p_appointment_date: appointment.appointment_date,
    p_appointment_time: appointment.appointment_time,
    p_notes: appointment.notes || null,
  });
  return assertResult(result);
}

export async function notifyAppointmentEmail(appointmentId) {
  if (!appointmentId) return { status: "skipped", reason: "missing_appointment_id" };
  const result = await ensureClient().functions.invoke("appointment-email-notify", {
    body: {
      clinic_id: CLINIC_ID,
      appointment_id: appointmentId,
    },
  });
  if (result.error) throw result.error;
  return result.data;
}

export async function loadPatientPortalAccessLog(limit = 100) {
  const result = await ensureClient().rpc("patient_portal_access_log", {
    p_clinic_id: CLINIC_ID,
    p_limit: limit,
  });
  return assertResult(result);
}

export async function resolveFuVisit(visitId) {
  const db = ensureClient();
  await db.from("visits")
    .update({ fu_resolved_at: new Date().toISOString() })
    .eq("id", visitId)
    .eq("clinic_id", CLINIC_ID);
}

// Resolve semua FU pending untuk satu klien (dipakai saat reservasi dibuat)
export async function resolveFuByClientId(clientId) {
  const db = ensureClient();
  await db.from("visits")
    .update({ fu_resolved_at: new Date().toISOString() })
    .eq("clinic_id", CLINIC_ID)
    .eq("client_id", clientId)
    .is("fu_resolved_at", null);
}

export async function loadExaminationFormData(visitId) {
  const result = await ensureClient().rpc("examination_form_data", {
    p_clinic_id: CLINIC_ID,
    p_visit_id: visitId,
  });
  return assertResult(result);
}

export async function saveClinicExamination(visitId, note, finalize = false) {
  const result = await ensureClient().rpc("save_clinic_examination", {
    p_clinic_id: CLINIC_ID,
    p_visit_id: visitId,
    p_note: note,
    p_finalize: finalize,
  });
  return assertResult(result);
}

export async function generateTcmExaminationDraft({ visit, client, form }) {
  const result = await ensureClient().functions.invoke("gemini-tcm-assist", {
    body: {
      clinic_id: CLINIC_ID,
      visit,
      client,
      form,
    },
  });
  if (result.error) {
    let message = result.error.message || String(result.error);
    try {
      const context = await result.error.context?.json?.();
      if (context?.error) message = context.error;
    } catch {
      // Supabase function errors do not always include a readable JSON body.
    }
    throw new Error(message);
  }
  return result.data;
}

export async function loadActionFormData(visitId) {
  const result = await ensureClient().rpc("action_form_data_operational", {
    p_clinic_id: CLINIC_ID,
    p_visit_id: visitId,
  });
  return assertResult(result);
}

export async function saveClinicAction(visitId, payload, finalize = false) {
  const result = await ensureClient().rpc("save_clinic_action_with_commissions", {
    p_clinic_id: CLINIC_ID,
    p_visit_id: visitId,
    p_payload: payload,
    p_finalize: finalize,
  });
  return assertResult(result);
}

export async function createClinicReferral({
  source_visit_id,
  target_division,
  reason,
  clinical_summary = null,
  target_nakes_id = null,
  target_doctor_name = null,
  priority = 'NORMAL',
}) {
  const result = await ensureClient().rpc('create_clinic_referral', {
    p_clinic_id: CLINIC_ID,
    p_source_visit_id: source_visit_id,
    p_target_division: target_division,
    p_reason: reason,
    p_clinical_summary: clinical_summary,
    p_target_nakes_id: target_nakes_id,
    p_target_doctor_name: target_doctor_name,
    p_priority: priority,
  });
  return assertResult(result);
}

export async function loadPaymentFormData(transactionId) {
  const db = ensureClient();
  const [detailResult, packageOptionsResult] = await Promise.all([
    db.rpc("payment_form_data", {
      p_clinic_id: CLINIC_ID,
      p_transaction_id: transactionId,
    }),
    db.rpc("payment_package_options", {
      p_clinic_id: CLINIC_ID,
      p_transaction_id: transactionId,
    }),
  ]);
  const detail = assertResult(detailResult);
  const visitId = detail?.transaction?.visit_id;

  let nakes_note = null;
  if (visitId) {
    const [estetikResult, medicalResult] = await Promise.all([
      db.from("estetik_notes")
        .select("action_notes,procedure_plan")
        .eq("visit_id", visitId)
        .maybeSingle(),
      db.from("medical_notes")
        .select("action_notes")
        .eq("clinic_id", CLINIC_ID)
        .eq("visit_id", visitId)
        .maybeSingle(),
    ]);
    const estetik = assertResult(estetikResult);
    const medical = assertResult(medicalResult);
    const followUp = estetik?.procedure_plan?.follow_up;
    const estetikNotes = estetik?.action_notes;
    const medicalNotes = medical?.action_notes;
    if (followUp || estetikNotes || medicalNotes) {
      nakes_note = {
        type: estetik ? "ESTETIK" : "WELLNESS",
        follow_up: followUp || null,
        action_notes: estetikNotes || medicalNotes || null,
      };
    }
  }

  // Muat data cicilan jika transaksi dalam mode cicilan
  const txStatus = detail?.transaction?.payment_status || '';
  const txMode   = detail?.transaction?.payment_mode   || 'FULL';
  let installments = [];
  if (txMode === 'INSTALLMENT' || /^PARTIAL$/i.test(txStatus)) {
    const instResult = await db
      .from('transaction_installments')
      .select('*')
      .eq('transaction_id', transactionId)
      .eq('clinic_id', CLINIC_ID)
      .order('installment_number', { ascending: true });
    assertResult(instResult);
    installments = instResult.data || [];
  }

  return {
    ...detail,
    package_options: assertResult(packageOptionsResult) || [],
    nakes_note,
    installments,
  };
}

export async function settleClinicPayment(transactionId, payment) {
  const result = await ensureClient().rpc("settle_clinic_payment", {
    p_clinic_id: CLINIC_ID,
    p_transaction_id: transactionId,
    p_payment: payment,
  });
  return assertResult(result);
}

export async function setReadyTransactionItemPackage(
  transactionItemId,
  clientPackageId = null,
) {
  const result = await ensureClient().rpc(
    "set_ready_transaction_item_package",
    {
      p_clinic_id: CLINIC_ID,
      p_transaction_item_id: transactionItemId,
      p_client_package_id: clientPackageId || null,
    },
  );
  return assertResult(result);
}

export async function loadPackageReferenceData() {
  const result = await ensureClient().rpc("package_reference_data", {
    p_clinic_id: CLINIC_ID,
  });
  return assertResult(result) || { clients: [], packages: [] };
}

export async function createPackagePurchase(clientId, packageId) {
  const result = await ensureClient().rpc("create_package_purchase", {
    p_clinic_id: CLINIC_ID,
    p_client_id: clientId,
    p_package_id: packageId,
  });
  return assertResult(result);
}

export async function resetVisitToSoap(visitId) {
  const result = await ensureClient()
    .from("visits")
    .update({ visit_status: "TERDAFTAR" })
    .eq("clinic_id", CLINIC_ID)
    .eq("id", visitId)
    .select("id,visit_status")
    .single();
  return assertResult(result);
}

export async function reopenVisitForCorrection(visitId) {
  const result = await ensureClient().rpc("reopen_visit_for_correction", {
    p_clinic_id: CLINIC_ID,
    p_visit_id: visitId,
  });
  return assertResult(result);
}

export async function loadEstetikFormData(visitId) {
  const result = await ensureClient().rpc("estetik_form_data", {
    p_clinic_id: CLINIC_ID,
    p_visit_id: visitId,
  });
  return assertResult(result);
}

export async function saveEstetikNote(visitId, note, finalize = false) {
  const result = await ensureClient().rpc("save_estetik_note", {
    p_clinic_id: CLINIC_ID,
    p_visit_id: visitId,
    p_note: note,
    p_finalize: finalize,
  });
  return assertResult(result);
}

export async function loadDentalFormData(visitId) {
  const result = await ensureClient().rpc("dental_form_data", {
    p_clinic_id: CLINIC_ID,
    p_visit_id: visitId,
  });
  return assertResult(result);
}

export async function saveDentalNote(visitId, note, finalize = false) {
  const result = await ensureClient().rpc("save_dental_note", {
    p_clinic_id: CLINIC_ID,
    p_visit_id: visitId,
    p_note: note,
    p_finalize: finalize,
  });
  return assertResult(result);
}

export async function loadDentalServices() {
  const result = await ensureClient()
    .from("services")
    .select("id,name,price,commission,duration,division,status")
    .eq("clinic_id", CLINIC_ID)
    .eq("division", "DENTAL")
    .eq("status", "ACTIVE")
    .order("name");
  return assertResult(result) || [];
}

export async function loadClientVisitHistory(clientId) {
  const db = ensureClient();
  const visitsRes = await db
    .from('visits')
    .select('id,visit_number,visit_date,doctor_name,division,visit_status,room_name')
    .eq('clinic_id', CLINIC_ID)
    .eq('client_id', clientId)
    .order('visit_date', { ascending: false })
    .limit(40);
  const visits = visitsRes.data || [];
  if (!visits.length) return [];
  const visitIds = visits.map((v) => v.id);
  const [duRes, estRes, txRes] = await Promise.all([
    db.from('dokter_umum_notes')
      .select('visit_id,current_complaint,diagnosis,icd10_primary,action_notes,nakes_id')
      .eq('clinic_id', CLINIC_ID).in('visit_id', visitIds),
    db.from('estetik_notes')
      .select('visit_id,chief_complaint,diagnosis,nakes_id')
      .eq('clinic_id', CLINIC_ID).in('visit_id', visitIds),
    db.from('transactions')
      .select('id,visit_id')
      .eq('clinic_id', CLINIC_ID).in('visit_id', visitIds),
  ]);
  const duMap  = Object.fromEntries((duRes.data  || []).map((n) => [n.visit_id, n]));
  const estMap = Object.fromEntries((estRes.data || []).map((n) => [n.visit_id, n]));
  const txIds  = (txRes.data || []).map((t) => t.id);
  const txVisitMap = Object.fromEntries((txRes.data || []).map((t) => [t.id, t.visit_id]));
  let tindakanMap = {};
  if (txIds.length) {
    const itemRes = await db.from('transaction_items')
      .select('transaction_id,services(name)')
      .in('transaction_id', txIds)
      .not('service_id', 'is', null)
      .neq('item_status', 'CANCELLED');
    for (const item of (itemRes.data || [])) {
      const vid = txVisitMap[item.transaction_id];
      const name = item.services?.name;
      if (vid && name) {
        if (!tindakanMap[vid]) tindakanMap[vid] = [];
        if (!tindakanMap[vid].includes(name)) tindakanMap[vid].push(name);
      }
    }
  }
  return visits.map((v) => {
    const du  = duMap[v.id];
    const est = estMap[v.id];
    return {
      ...v,
      keluhan:   du?.current_complaint || est?.chief_complaint || '',
      diagnosa:  du?.diagnosis || est?.diagnosis || '',
      icd10:     du?.icd10_primary || null,
      catatan:   du?.action_notes || '',
      nakes_id:  du?.nakes_id || est?.nakes_id || null,
      tindakan:  tindakanMap[v.id] || [],
    };
  });
}

export async function loadDokterUmumFormData(visitId) {
  const result = await ensureClient().rpc("dokter_umum_form_data", {
    p_clinic_id: CLINIC_ID,
    p_visit_id: visitId,
  });
  return assertResult(result);
}

export async function saveDokterUmumNote(visitId, note, finalize = false) {
  const result = await ensureClient().rpc("save_dokter_umum_note", {
    p_clinic_id: CLINIC_ID,
    p_visit_id: visitId,
    p_note: note,
    p_finalize: finalize,
  });
  return assertResult(result);
}

// Daftar obat dari BHP yang bisa diresepkan dokter (untuk autocomplete)
export async function loadPharmacyDrugs() {
  const result = await ensureClient()
    .from("bhp_items")
    .select("id,item_code,name,unit,division,category")
    .eq("clinic_id", CLINIC_ID)
    .eq("division", "APOTIK")
    .eq("status", "ACTIVE")
    .order("name");
  return assertResult(result) || [];
}

export async function loadPharmacyQueue(date) {
  const result = await ensureClient().rpc("pharmacy_queue", {
    p_clinic_id: CLINIC_ID,
    p_date: date,
  });
  return assertResult(result);
}

export async function savePharmacyDispensing(visitId, apotekerId, items, notes, finalize = false) {
  const result = await ensureClient().rpc("save_pharmacy_dispensing", {
    p_clinic_id: CLINIC_ID,
    p_visit_id: visitId,
    p_apoteker_id: apotekerId,
    p_items: items,
    p_notes: notes,
    p_finalize: finalize,
  });
  return assertResult(result);
}

export async function addVisitPhoto(visitId, photo) {
  const result = await ensureClient().rpc("add_visit_photo", {
    p_clinic_id: CLINIC_ID,
    p_visit_id: visitId,
    p_photo: photo,
  });
  return assertResult(result);
}

export async function loadVisitPhotos(visitId) {
  const result = await ensureClient()
    .from("visit_photos")
    .select("id,photo_type,storage_url,gdrive_url,gdrive_file_id,thumbnail_url,notes,created_at")
    .eq("clinic_id", CLINIC_ID)
    .eq("visit_id", visitId)
    .order("created_at", { ascending: true });
  return assertResult(result) || [];
}

export async function recordFollowUpContact({
  visitId,
  clientId,
  contactedBy,
  messageText,
  method = "WHATSAPP",
  resultStatus = null,
  responseText = null,
  nextActionDate = null,
}) {
  const payload = {
    clinic_id:    CLINIC_ID,
    visit_id:     visitId,
    client_id:    clientId,
    contacted_by: contactedBy || null,
    method,
    message_text: messageText || null,
    result_status: resultStatus || null,
    response_text: responseText || null,
    next_action_date: nextActionDate || null,
  };
  const result = await ensureClient().from("follow_up_contacts").insert(payload);
  if (!result.error) return result.data;
  if (!/result_status|response_text|next_action_date|schema cache|does not exist/i.test(result.error.message || "")) {
    throw result.error;
  }
  const fallback = await ensureClient()
    .from("follow_up_contacts")
    .insert({
      clinic_id:    CLINIC_ID,
      visit_id:     visitId,
      client_id:    clientId,
      contacted_by: contactedBy || null,
      method,
      message_text: [messageText, responseText, nextActionDate ? `Next action: ${nextActionDate}` : ""]
        .filter(Boolean)
        .join("\n\n"),
    });
  return assertResult(fallback);
}

export async function loadFollowUpContacts(visitIds) {
  if (!visitIds?.length) return {};
  const result = await ensureClient()
    .from("follow_up_contacts")
    .select("visit_id,contacted_at,contacted_by,method")
    .eq("clinic_id", CLINIC_ID)
    .in("visit_id", visitIds)
    .order("contacted_at", { ascending: false });
  const rows = assertResult(result) || [];
  // Return map: visit_id → most recent contact
  const map = {};
  for (const row of rows) {
    if (!map[row.visit_id]) map[row.visit_id] = row;
  }
  return map;
}

function daysBetween(dateKey, value) {
  if (!value) return null;
  const start = new Date(`${String(value).slice(0, 10)}T00:00:00+07:00`);
  const end = new Date(`${dateKey}T00:00:00+07:00`);
  const days = Math.floor((end.getTime() - start.getTime()) / 86400000);
  return Number.isFinite(days) ? days : null;
}

function followUpBucket(daysSinceVisit) {
  if (daysSinceVisit >= 1 && daysSinceVisit <= 3) return "newWarm";
  if (daysSinceVisit >= 7 && daysSinceVisit <= 14) return "routine";
  if (daysSinceVisit > 30 && daysSinceVisit <= 90) return "dormant";
  return null;
}

const FOLLOW_UP_QUOTAS = {
  newWarm: 7,
  routine: 7,
  dormant: 6,
};

const FOLLOW_UP_LABELS = {
  newWarm: "Klien Baru / Hangat",
  routine: "Klien Rutin / Mingguan",
  dormant: "Klien Lama / Dormant",
};

function normalizedVisitStatus(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

function isCanceledVisit(visit) {
  return normalizedVisitStatus(visit?.visit_status).includes("BATAL");
}

function isActiveVisit(visit) {
  const status = normalizedVisitStatus(visit?.visit_status);
  return ["TERDAFTAR", "ANTRIAN", "PEMERIKSAAN", "TINDAKAN", "IN_PROGRESS"].includes(status);
}

function isCompletedVisit(visit) {
  const status = normalizedVisitStatus(visit?.visit_status);
  return status.includes("SELESAI")
    || ["LUNAS", "PAID", "SIAP_BAYAR", "READY_FOR_PAYMENT", "SETTLED"].includes(status);
}

export async function loadFollowUpDailyQueue({ division = null, cooldownDays = 7, marketerUserId = null } = {}) {
  const db = ensureClient();
  const today = jakartaDateKey();
  const since = new Date(`${today}T00:00:00+07:00`);
  since.setDate(since.getDate() - 120);
  const sinceStr = since.toISOString().slice(0, 10);

  let assignedClientIds = null;
  if (marketerUserId) {
    const referralResult = await db
      .from("marketing_referral_links")
      .select("referred_client_id")
      .eq("clinic_id", CLINIC_ID)
      .eq("marketer_user_id", marketerUserId)
      .eq("status", "ACTIVE");
    const referralRows = assertResult(referralResult) || [];
    assignedClientIds = [...new Set(referralRows.map((row) => row.referred_client_id).filter(Boolean))];
    if (!assignedClientIds.length) {
      return {
        today,
        cooldownDays,
        quotas: FOLLOW_UP_QUOTAS,
        labels: FOLLOW_UP_LABELS,
        rows: [],
        locked: [],
        assignedClientCount: 0,
        summary: { total: 0, available: 0, locked: 0 },
      };
    }
  }

  let visitQuery = db
    .from("visits")
    .select("id,visit_number,client_id,visit_date,division,doctor_name,room_name,visit_status,payment_status")
    .eq("clinic_id", CLINIC_ID)
    .gte("visit_date", sinceStr)
    .order("visit_date", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(800);
  if (division) visitQuery = visitQuery.ilike("division", division);
  if (assignedClientIds) visitQuery = visitQuery.in("client_id", assignedClientIds);

  const visits = assertResult(await visitQuery) || [];
  if (!visits.length) {
    return {
      today,
      cooldownDays,
      quotas: FOLLOW_UP_QUOTAS,
      labels: FOLLOW_UP_LABELS,
      rows: [],
      locked: [],
      summary: { total: 0, available: 0, locked: 0 },
    };
  }

  const activeTodayClientIds = new Set(
    visits
      .filter((visit) => String(visit.visit_date).slice(0, 10) === today && isActiveVisit(visit))
      .map((visit) => visit.client_id)
      .filter(Boolean),
  );
  const latestVisitByClient = {};
  for (const visit of visits) {
    if (
      visit.client_id
      && !latestVisitByClient[visit.client_id]
      && isCompletedVisit(visit)
      && !isCanceledVisit(visit)
      && !activeTodayClientIds.has(visit.client_id)
    ) {
      latestVisitByClient[visit.client_id] = visit;
    }
  }
  if (!Object.keys(latestVisitByClient).length) {
    return {
      today,
      cooldownDays,
      quotas: FOLLOW_UP_QUOTAS,
      labels: FOLLOW_UP_LABELS,
      rows: [],
      locked: [],
      assignedClientCount: assignedClientIds?.length ?? null,
      summary: { total: 0, available: 0, locked: 0 },
    };
  }

  const clients = await clientMap(Object.keys(latestVisitByClient));
  const clientIds = Object.keys(clients);
  if (!clientIds.length) {
    return {
      today,
      cooldownDays,
      quotas: FOLLOW_UP_QUOTAS,
      labels: FOLLOW_UP_LABELS,
      rows: [],
      locked: [],
      summary: { total: 0, available: 0, locked: 0 },
    };
  }

  const contactsResult = await db
    .from("follow_up_contacts")
    .select("client_id,visit_id,contacted_at,contacted_by,method")
    .eq("clinic_id", CLINIC_ID)
    .in("client_id", clientIds)
    .order("contacted_at", { ascending: false });
  if (contactsResult.error) {
    throw new Error(`Akses follow_up_contacts ditolak: ${contactsResult.error.message}`);
  }
  const contacts = contactsResult.data || [];
  const lastContactByClient = {};
  for (const contact of contacts) {
    if (contact.client_id && !lastContactByClient[contact.client_id]) {
      lastContactByClient[contact.client_id] = contact;
    }
  }

  const visitIds = Object.values(latestVisitByClient).map((visit) => visit.id);
  const [medicalResult, estetikResult] = marketerUserId
    ? [{ data: [] }, { data: [] }]
    : await Promise.all([
      db.from("medical_notes")
        .select("visit_id,action_notes,current_complaint,syndrome_status")
        .eq("clinic_id", CLINIC_ID)
        .in("visit_id", visitIds),
      db.from("estetik_notes")
        .select("visit_id,action_notes,procedure_plan,current_complaint")
        .in("visit_id", visitIds),
    ]);
  const medicalMap = Object.fromEntries((assertResult(medicalResult) || []).map((note) => [note.visit_id, note]));
  const estetikMap = Object.fromEntries((assertResult(estetikResult) || []).map((note) => [note.visit_id, note]));

  const candidates = [];
  const locked = [];
  for (const [clientId, visit] of Object.entries(latestVisitByClient)) {
    const daysSinceVisit = daysBetween(today, visit.visit_date);
    const bucket = followUpBucket(daysSinceVisit);
    if (!bucket) continue;

    const lastContact = lastContactByClient[clientId] || null;
    const daysSinceContact = daysBetween(today, lastContact?.contacted_at);
    const cooldownUntilDate = lastContact?.contacted_at
      ? new Date(`${String(lastContact.contacted_at).slice(0, 10)}T00:00:00+07:00`)
      : null;
    if (cooldownUntilDate) cooldownUntilDate.setDate(cooldownUntilDate.getDate() + cooldownDays);
    const cooldownUntil = cooldownUntilDate ? cooldownUntilDate.toISOString().slice(0, 10) : null;
    const inCooldown = daysSinceContact !== null && daysSinceContact < cooldownDays;

    const estetik = estetikMap[visit.id];
    const medical = medicalMap[visit.id];
    const row = {
      ...visit,
      client_id: clientId,
      client: clients[clientId],
      bucket,
      bucket_label: FOLLOW_UP_LABELS[bucket],
      quota: FOLLOW_UP_QUOTAS[bucket],
      days_since_visit: daysSinceVisit,
      last_contact: lastContact,
      days_since_contact: daysSinceContact,
      cooldown_until: cooldownUntil,
      cooldown_locked: inCooldown,
      note_type: estetik ? "ESTETIK" : "WELLNESS",
      follow_up: estetik?.procedure_plan?.follow_up || null,
      action_notes: estetik?.action_notes || medical?.action_notes || null,
      current_complaint: estetik?.current_complaint || medical?.current_complaint || null,
      syndrome_status: medical?.syndrome_status || null,
      fu_status: inCooldown ? "COOLDOWN" : "BELUM",
    };
    if (inCooldown) locked.push(row);
    else candidates.push(row);
  }

  const byBucket = { newWarm: [], routine: [], dormant: [] };
  for (const row of candidates) byBucket[row.bucket]?.push(row);
  for (const key of Object.keys(byBucket)) {
    byBucket[key].sort((a, b) => b.days_since_visit - a.days_since_visit);
  }

  const rows = [
    ...byBucket.newWarm.slice(0, FOLLOW_UP_QUOTAS.newWarm),
    ...byBucket.routine.slice(0, FOLLOW_UP_QUOTAS.routine),
    ...byBucket.dormant.slice(0, FOLLOW_UP_QUOTAS.dormant),
  ];

  return {
    today,
    cooldownDays,
    quotas: FOLLOW_UP_QUOTAS,
    labels: FOLLOW_UP_LABELS,
    rows,
    locked,
    availableByBucket: Object.fromEntries(Object.entries(byBucket).map(([key, value]) => [key, value.length])),
    summary: {
      total: rows.length,
      available: candidates.length,
      locked: locked.length,
    },
    assignedClientCount: assignedClientIds?.length ?? null,
  };
}

export async function loadInformedConsent(visitId) {
  const result = await ensureClient()
    .from("informed_consents")
    .select("id,visit_id,procedures_listed,storage_url,signed_at,created_at")
    .eq("clinic_id", CLINIC_ID)
    .eq("visit_id", visitId)
    .maybeSingle();
  return assertResult(result);
}

export async function loadFollowUpData({ division = null, days = 90 } = {}) {
  const db = ensureClient();
  const since = new Date();
  since.setDate(since.getDate() - days);
  const sinceStr = since.toISOString().slice(0, 10);

  // Load completed visits in date range
  let query = db
    .from("visits")
    .select("id,visit_number,visit_date,division,doctor_name,visit_status,payment_status,client_id")
    .eq("clinic_id", CLINIC_ID)
    .gte("visit_date", sinceStr)
    .not("visit_status", "ilike", "TERDAFTAR")
    .not("visit_status", "ilike", "TINDAKAN")
    .is("fu_resolved_at", null)
    .order("visit_date", { ascending: false })
    .limit(200);
  if (division) query = query.ilike("division", division);

  const visits = assertResult(await query) || [];
  if (!visits.length) return [];

  const visitIds = visits.map((v) => v.id);

  // Batch fetch notes
  const [medicalResult, estetikResult] = await Promise.all([
    db.from("medical_notes")
      .select("visit_id,action_notes,draft_status")
      .eq("clinic_id", CLINIC_ID)
      .in("visit_id", visitIds),
    db.from("estetik_notes")
      .select("visit_id,action_notes,procedure_plan,draft_status")
      .in("visit_id", visitIds),
  ]);
  const medicalMap = Object.fromEntries(
    (assertResult(medicalResult) || []).map((n) => [n.visit_id, n])
  );
  const estetikMap = Object.fromEntries(
    (assertResult(estetikResult) || []).map((n) => [n.visit_id, n])
  );

  // Filter: only visits that have a follow_up or action_notes
  const withNotes = visits
    .map((v) => {
      const en = estetikMap[v.id];
      const mn = medicalMap[v.id];
      const followUp = en?.procedure_plan?.follow_up || null;
      const notes = en?.action_notes || mn?.action_notes || null;
      if (!followUp && !notes) return null;
      return {
        ...v,
        note_type: en ? "ESTETIK" : "WELLNESS",
        follow_up: followUp,
        action_notes: notes,
      };
    })
    .filter(Boolean);

  // Fetch tindakan per visit dari transaction_items
  const visitIdList = withNotes.map((v) => v.id);
  let tindakanMap = {};
  if (visitIdList.length) {
    const txResult = await db
      .from("transactions")
      .select("visit_id,id")
      .eq("clinic_id", CLINIC_ID)
      .in("visit_id", visitIdList);
    const txRows = assertResult(txResult) || [];
    const txIds = txRows.map((t) => t.id);
    const txVisitMap = Object.fromEntries(txRows.map((t) => [t.id, t.visit_id]));
    if (txIds.length) {
      const itemResult = await db
        .from("transaction_items")
        .select("transaction_id,service_id,services(name)")
        .in("transaction_id", txIds)
        .not("service_id", "is", null);
      const itemRows = assertResult(itemResult) || [];
      for (const item of itemRows) {
        const vid = txVisitMap[item.transaction_id];
        const name = item.services?.name;
        if (!vid || !name) continue;
        if (!tindakanMap[vid]) tindakanMap[vid] = [];
        if (!tindakanMap[vid].includes(name))
          tindakanMap[vid].push(name);
      }
    }
  }

  const clientData = await clientMap(withNotes.map((v) => v.client_id));
  return attachClients(
    withNotes.map((v) => ({ ...v, tindakan: tindakanMap[v.id] || [] })),
    clientData,
  );
}

export async function saveInformedConsent(visitId, payload) {
  const db = ensureClient();
  const existing = await loadInformedConsent(visitId);
  if (existing) {
    const result = await db
      .from("informed_consents")
      .update(payload)
      .eq("id", existing.id);
    return assertResult(result);
  } else {
    const result = await db
      .from("informed_consents")
      .insert({ clinic_id: CLINIC_ID, visit_id: visitId, ...payload });
    return assertResult(result);
  }
}

export async function loadClinicSettings() {
  const result = await ensureClient().rpc("clinic_settings_data", {
    p_clinic_id: CLINIC_ID,
  });
  return assertResult(result) || {};
}

// ── BHP Requests (permintaan stok internal ke gudang) ──────────────────────

// Gabung bhp_items (master catalog) + bhp_stock (stok per lokasi)
// location: 'GUDANG' | 'WELLNESS' | 'ESTETIK' | 'DENTAL'
export async function loadBhpItemsWithStock(location = "GUDANG") {
  if (location === "ALL") return loadBhpItemsAll();

  const db = ensureClient();
  const [itemsRes, stockRes] = await Promise.all([
    db.from("bhp_items")
      .select("id,item_code,name,category,unit,base_unit,purchase_unit,conversion_factor,division,status,updated_at")
      .eq("clinic_id", CLINIC_ID)
      .order("name"),
    db.from("bhp_stock")
      .select("item_id,current_stock,minimum_stock,target_stock")
      .eq("clinic_id", CLINIC_ID)
      .eq("location", location),
  ]);
  const items = assertResult(itemsRes) || [];
  const stock = assertResult(stockRes) || [];
  const stockMap = Object.fromEntries(stock.map((s) => [s.item_id, s]));
  let usageRows = [];
  const usageResult = await db.from("bhp_item_divisions").select("bhp_item_id,division").eq("clinic_id", CLINIC_ID);
  if (!usageResult.error) usageRows = usageResult.data || [];
  const usageMap = {};
  usageRows.forEach((row) => {
    if (!usageMap[row.bhp_item_id]) usageMap[row.bhp_item_id] = [];
    usageMap[row.bhp_item_id].push(row.division);
  });
  return items.map((item) => ({
    ...item,
    divisions: usageMap[item.id]?.length ? usageMap[item.id] : item.division === "ALL" ? null : [item.division],
    stock_unit: location === "GUDANG" ? (item.purchase_unit || item.unit) : (item.base_unit || item.unit),
    current_stock: stockMap[item.id]?.current_stock ?? 0,
    minimum_stock: stockMap[item.id]?.minimum_stock ?? 0,
    target_stock:  stockMap[item.id]?.target_stock  ?? 0,
    stock_location: location,
  }));
}

export async function loadBhpItemsAll() {
  const db = ensureClient();
  const [itemsRes, stockRes] = await Promise.all([
    db.from("bhp_items")
      .select("id,item_code,name,category,unit,base_unit,purchase_unit,conversion_factor,division,status,updated_at")
      .eq("clinic_id", CLINIC_ID)
      .order("name"),
    db.from("bhp_stock")
      .select("item_id,location,current_stock,minimum_stock,target_stock")
      .eq("clinic_id", CLINIC_ID),
  ]);
  const items = assertResult(itemsRes) || [];
  const stock = assertResult(stockRes) || [];
  let usageRows = [];
  const usageResult = await db.from("bhp_item_divisions").select("bhp_item_id,division").eq("clinic_id", CLINIC_ID);
  if (!usageResult.error) usageRows = usageResult.data || [];
  const usageMap = {};
  usageRows.forEach((row) => {
    if (!usageMap[row.bhp_item_id]) usageMap[row.bhp_item_id] = [];
    usageMap[row.bhp_item_id].push(row.division);
  });
  const stockByItem = {};
  for (const row of stock) {
    if (!stockByItem[row.item_id]) stockByItem[row.item_id] = {};
    stockByItem[row.item_id][row.location] = row;
  }
  return items.map((item) => {
    const locations = stockByItem[item.id] || {};
    const gudang = Number(locations.GUDANG?.current_stock || 0);
    const wellness = Number(locations.WELLNESS?.current_stock || 0);
    const estetik = Number(locations.ESTETIK?.current_stock || 0);
    const dental = Number(locations.DENTAL?.current_stock || 0);
    const dokterUmum = Number(locations.DOKTER_UMUM?.current_stock || 0);
    const apotik = Number(locations.APOTIK?.current_stock || 0);
    return {
      ...item,
      divisions: usageMap[item.id]?.length ? usageMap[item.id] : item.division === "ALL" ? null : [item.division],
      gudang_stock: gudang,
      wellness_stock: wellness,
      estetik_stock: estetik,
      dental_stock: dental,
      dokter_umum_stock: dokterUmum,
      apotik_stock: apotik,
      current_stock: gudang + wellness + estetik + dental + dokterUmum + apotik,
      stock_unit: item.purchase_unit || item.unit,
      minimum_stock: locations.GUDANG?.minimum_stock ?? 0,
      target_stock: locations.GUDANG?.target_stock ?? 0,
    };
  });
}

// Update minimum/target stok per lokasi di bhp_stock
export async function updateBhpStockLimits(itemId, location, { minimum_stock, target_stock }) {
  const db = ensureClient();
  const { error } = await db.from("bhp_stock")
    .upsert({ clinic_id: CLINIC_ID, item_id: itemId, location, minimum_stock, target_stock },
      { onConflict: "clinic_id,item_id,location" });
  if (error) throw error;
}

// Inisialisasi bhp_stock untuk item baru (semua lokasi = 0)
export async function initBhpStockForItem(itemId) {
  const db = ensureClient();
  const rows = ["GUDANG", "WELLNESS", "ESTETIK", "DENTAL", "DOKTER_UMUM", "APOTIK"].map((loc) => ({
    clinic_id: CLINIC_ID, item_id: itemId, location: loc,
    current_stock: 0, minimum_stock: 0, target_stock: 0,
  }));
  const { error } = await db.from("bhp_stock").upsert(rows, { onConflict: "clinic_id,item_id,location" });
  if (error) throw error;
}

export async function loadBhpRequests({ status = null } = {}) {
  const db = ensureClient();
  let q = db
    .from("bhp_requests")
    .select("id,bhp_item_id,item_name,unit,requested_qty,status,notes,requested_by,from_division,requested_at,processed_at,shipped_at,received_at,received_by,received_qty")
    .eq("clinic_id", CLINIC_ID)
    .order("requested_at", { ascending: false })
    .limit(200);
  if (status) q = q.eq("status", status);
  return assertResult(await q) || [];
}

export async function createBhpRequest({ bhp_item_id, item_name, unit, requested_qty, notes, requested_by, from_division }) {
  const db = ensureClient();
  return assertResult(await db.from("bhp_requests").insert({
    clinic_id: CLINIC_ID,
    bhp_item_id: bhp_item_id || null,
    item_name,
    unit,
    requested_qty: Number(requested_qty) || 1,
    notes: notes || null,
    requested_by: requested_by || null,
    // Legacy column name: this stores the destination stock location.
    from_division: from_division || null,  // WELLNESS | ESTETIK | DENTAL | DOKTER_UMUM | APOTIK
    status: "REQUESTED",
    requested_at: new Date().toISOString(),
  }).select().single());
}

export async function updateBhpRequestStatus(id, status, extra = {}) {
  const db = ensureClient();
  if (status === "DITERIMA") {
    return assertResult(await db.rpc("receive_bhp_request", {
      p_clinic_id: CLINIC_ID,
      p_request_id: id,
      p_received_qty: Number(extra.received_qty),
      p_received_by: extra.received_by || null,
    }));
  }
  const now = new Date().toISOString();
  const patch = { status };
  if (status === "DIPROSES")   patch.processed_at = now;
  if (status === "DIKIRIM")    patch.shipped_at   = now;
  if (status === "DITERIMA") {
    patch.received_at  = now;
    patch.received_by  = extra.received_by  || null;
    patch.received_qty = extra.received_qty != null ? Number(extra.received_qty) : null;
  }
  assertResult(await db.from("bhp_requests").update(patch).eq("id", id));
}

// ── Survey Kepuasan Pasien ──────────────────────────────────────────────────

// Katalog default — digunakan sebagai fallback jika belum dikonfigurasi di Pengaturan
export const PRIZE_CATALOG_DEFAULT = [
  { type: 'DISCOUNT_20K',  label: 'Diskon Rp 20.000',       emoji: '💸', desc: 'Potongan Rp 20.000 dari total tagihan kunjungan berikutnya',  notes: '',  discount: 20000,  quota: 0, weight: 35 },
  { type: 'DISCOUNT_50K',  label: 'Diskon Rp 50.000',       emoji: '🎫', desc: 'Potongan Rp 50.000 dari total tagihan kunjungan berikutnya',  notes: '',  discount: 50000,  quota: 0, weight: 25 },
  { type: 'DISCOUNT_100K', label: 'Diskon Rp 100.000',      emoji: '🏷️', desc: 'Potongan Rp 100.000 dari total tagihan kunjungan berikutnya', notes: '',  discount: 100000, quota: 0, weight: 15 },
  { type: 'FACIAL_20K',    label: 'Trial Facial Rp 20.000', emoji: '✨', desc: '1x sesi facial dengan tarif spesial hanya Rp 20.000',         notes: 'Dapat diklaim untuk treatment facial reguler', discount: 0, quota: 0, weight: 20 },
  { type: 'FREE_PRODUCT',  label: 'Free Skincare Sample',   emoji: '🎁', desc: 'Gratis 1 paket skincare sample eksklusif dari klinik',         notes: '',  discount: 0,      quota: 0, weight: 5  },
];

// Untuk backward compat
export const PRIZE_CATALOG = PRIZE_CATALOG_DEFAULT;

// Load katalog dari clinic_settings (key: prize_catalog), fallback ke default
// ── Role & Akses ────────────────────────────────────────────────────────────

export async function loadRoles() {
  const db = ensureClient();
  const { data, error } = await db
    .from('roles')
    .select('id,role_name,menu_access,can_see_revenue_today,can_see_revenue_month')
    .order('role_name');
  if (error) throw error;
  return data || [];
}

export async function saveRole(role) {
  const db = ensureClient();
  const payload = {
    id:                    role.id,
    role_name:             role.role_name,
    menu_access:           role.menu_access,
    can_see_revenue_today: role.can_see_revenue_today  || false,
    can_see_revenue_month: role.can_see_revenue_month  || false,
  };
  const { error } = await db.from('roles').upsert(payload, { onConflict: 'id' });
  if (error) throw error;
}

export async function deleteRole(roleId) {
  const db = ensureClient();
  const { error } = await db.from('roles').delete().eq('id', roleId);
  if (error) throw error;
}

// ── Prize Catalog ────────────────────────────────────────────────────────────

export async function loadPrizeCatalog() {
  const db = ensureClient();
  const { data } = await db
    .from('clinic_settings')
    .select('value')
    .eq('clinic_id', CLINIC_ID)
    .eq('key', 'prize_catalog')
    .maybeSingle();
  if (data?.value) {
    try { return JSON.parse(data.value); } catch { /* fallback */ }
  }
  return PRIZE_CATALOG_DEFAULT;
}

// Simpan katalog ke clinic_settings
export async function savePrizeCatalog(prizes) {
  const db = ensureClient();
  const { error } = await db.from('clinic_settings').upsert({
    clinic_id: CLINIC_ID,
    key: 'prize_catalog',
    value: JSON.stringify(prizes),
  }, { onConflict: 'clinic_id,key' });
  if (error) throw error;
}

export function getPrize(type, catalog) {
  const list = catalog || PRIZE_CATALOG_DEFAULT;
  return list.find((p) => p.type === type) || null;
}

function randomPrizeFromCatalog(catalog, usageCounts) {
  // Filter: kuota 0 = unlimited; kuota > 0 = hanya boleh jika sisa > 0
  const available = catalog.filter((p) => {
    if (!p.quota || p.quota === 0) return true;
    return (usageCounts[p.type] || 0) < p.quota;
  });
  if (!available.length) return catalog[0]?.type || 'DISCOUNT_20K';
  const total = available.reduce((s, p) => s + (p.weight || 1), 0);
  let r = Math.random() * total;
  for (const p of available) {
    r -= (p.weight || 1);
    if (r <= 0) return p.type;
  }
  return available[0].type;
}

function makeVoucherCode() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "VCR-";
  for (let i = 0; i < 8; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

export async function createSurveyToken({ visit_id, client_id, client_name, client_phone }) {
  const db = ensureClient();
  let durationMinutes = 60;
  try {
    const settings = await loadClinicSettings();
    const configured = Number(settings?.contact?.survey_expiry_minutes);
    if (Number.isFinite(configured) && configured >= 5) durationMinutes = Math.min(configured, 24 * 60);
  } catch {
    // Keep the safe default when settings cannot be read during payment flow.
  }
  const expires_at = new Date(Date.now() + durationMinutes * 60 * 1000).toISOString();
  const result = await db
    .from("survey_tokens")
    .insert({
      clinic_id: CLINIC_ID,
      visit_id:  visit_id  || null,
      client_id: client_id || null,
      client_name:  client_name  || null,
      client_phone: client_phone || null,
      expires_at,
    })
    .select("token")
    .single();
  return assertResult(result); // { token: "..." }
}

export async function getSurveyByToken(token) {
  const { data, error } = await ensureClient()
    .from("survey_tokens")
    .select("id,token,clinic_id,client_name,expires_at,submitted_at,rating,voucher_code,voucher_used,prize_type")
    .eq("token", token)
    .single();
  if (error) throw error;
  return data;
}

export async function submitSurvey(token, { rating, comment }) {
  const db = ensureClient();
  // Cek token valid dan belum diisi
  const { data: row, error: fetchErr } = await db
    .from("survey_tokens")
    .select("id,expires_at,submitted_at")
    .eq("token", token)
    .single();
  if (fetchErr || !row) throw new Error("Link survey tidak valid.");
  if (row.submitted_at) throw new Error("Survey sudah pernah diisi.");
  const now = new Date();
  const expired = new Date(row.expires_at) < now;
  const voucher_code = !expired ? makeVoucherCode() : null;
  const { error: updateErr } = await db
    .from("survey_tokens")
    .update({
      submitted_at: now.toISOString(),
      rating,
      comment: comment || null,
      voucher_code,
      // prize_type ditentukan saat kasir klaim di halaman Voucher
    })
    .eq("token", token);
  if (updateErr) throw updateErr;
  return { expired, voucher_code };
}

export async function useVoucher(token) {
  const db = ensureClient();
  const { data: row } = await db
    .from("survey_tokens")
    .select("voucher_code,voucher_used,submitted_at")
    .eq("token", token)
    .single();
  if (!row?.voucher_code) throw new Error("Voucher tidak ditemukan.");
  if (row.voucher_used) throw new Error("Voucher sudah digunakan.");
  await db.from("survey_tokens")
    .update({ voucher_used: true, voucher_used_at: new Date().toISOString() })
    .eq("token", token);
  return row.voucher_code;
}

// Kasir: cek status lengkap voucher (untuk halaman Voucher)
export async function checkVoucherStatus(code) {
  const db = ensureClient();
  const { data: row } = await db
    .from("survey_tokens")
    .select("id,voucher_code,voucher_used,voucher_used_at,prize_type,client_name,submitted_at,expires_at,rating")
    .eq("voucher_code", code.trim().toUpperCase())
    .eq("clinic_id", CLINIC_ID)
    .maybeSingle();

  if (!row) return { status: "not_found" };
  if (row.voucher_used) return { status: "used", row };

  // Voucher kadaluwarsa jika sudah 30 hari sejak submitted
  const submittedDate = new Date(row.submitted_at);
  const expiredDate   = new Date(submittedDate.getTime() + 30 * 24 * 60 * 60 * 1000);
  if (new Date() > expiredDate) return { status: "expired", row, expiredDate };

  // Prize sudah di-assign tapi belum dipakai (misalnya DISCOUNT yg belum diapply)
  if (row.prize_type) return { status: "claimed_not_used", row };

  return { status: "valid", row };
}

// Kasir: klaim voucher → assign prize random dengan kuota, untuk non-DISCOUNT langsung tandai used
export async function claimVoucherPrize(code) {
  const db = ensureClient();
  const { data: row } = await db
    .from("survey_tokens")
    .select("id,voucher_used,prize_type")
    .eq("voucher_code", code.trim().toUpperCase())
    .eq("clinic_id", CLINIC_ID)
    .maybeSingle();

  if (!row) throw new Error("Voucher tidak ditemukan.");
  if (row.voucher_used) throw new Error("Voucher sudah digunakan.");

  // Jika prize sudah diassign sebelumnya, kembalikan saja
  const catalog = await loadPrizeCatalog();
  if (row.prize_type) return getPrize(row.prize_type, catalog);

  // Hitung penggunaan per prize_type untuk kuota
  const { data: usageRows } = await db
    .from("survey_tokens")
    .select("prize_type")
    .eq("clinic_id", CLINIC_ID)
    .not("prize_type", "is", null);
  const usageCounts = {};
  for (const u of (usageRows || [])) {
    usageCounts[u.prize_type] = (usageCounts[u.prize_type] || 0) + 1;
  }

  const prize_type = randomPrizeFromCatalog(catalog, usageCounts);
  const prize = getPrize(prize_type, catalog);
  const isDiscount = (prize?.discount || 0) > 0;

  const updatePayload = {
    prize_type,
    // Non-discount: langsung tandai used di sini
    ...(isDiscount ? {} : { voucher_used: true, voucher_used_at: new Date().toISOString() }),
  };

  const { error } = await db.from("survey_tokens")
    .update(updatePayload)
    .eq("voucher_code", code.trim().toUpperCase())
    .eq("clinic_id", CLINIC_ID);
  if (error) throw error;

  return prize;
}

// Kasir: validasi kode voucher tanpa memakai
export async function checkVoucherByCode(code) {
  const db = ensureClient();
  const normalizedCode = code.trim().toUpperCase();
  const { data: row } = await db
    .from("survey_tokens")
    .select("id,voucher_code,voucher_used,prize_type,client_name,submitted_at")
    .eq("voucher_code", normalizedCode)
    .eq("clinic_id", CLINIC_ID)
    .maybeSingle();
  if (row) {
    if (row.voucher_used) throw new Error("Voucher ini sudah pernah digunakan.");
    if (!row.prize_type) throw new Error("Voucher tidak memiliki hadiah.");
    return row;
  }

  const { data: mgmVoucher, error: mgmError } = await db
    .from("mgm_treatment_vouchers")
    .select("id,voucher_code,used,discount_amount,member_client:clients!member_client_id(name)")
    .eq("voucher_code", normalizedCode)
    .eq("clinic_id", CLINIC_ID)
    .maybeSingle();
  if (mgmError) throw mgmError;
  if (!mgmVoucher) throw new Error("Kode voucher tidak ditemukan.");
  if (mgmVoucher.used) throw new Error("Voucher ini sudah pernah digunakan.");
  return {
    ...mgmVoucher,
    voucher_used: mgmVoucher.used,
    prize_type: "MGM_TREATMENT_CREDIT",
    client_name: mgmVoucher.member_client?.name || "Member MGM",
    voucher_source: "MGM",
  };
}

// Kasir: pakai voucher — untuk DISCOUNT, sekaligus update diskon transaksi
export async function redeemVoucherOnTransaction(voucherCode, transactionId, discountAmount) {
  const db = ensureClient();
  const normalizedCode = voucherCode.trim().toUpperCase();
  const { data: mgmVoucher, error: mgmLookupError } = await db
    .from("mgm_treatment_vouchers")
    .select("id,used,discount_amount")
    .eq("voucher_code", normalizedCode)
    .eq("clinic_id", CLINIC_ID)
    .maybeSingle();
  if (mgmLookupError) throw mgmLookupError;
  if (mgmVoucher) {
    if (mgmVoucher.used) throw new Error("Voucher sudah digunakan.");
    const appliedDiscount = Math.min(Number(discountAmount || mgmVoucher.discount_amount || 0), Number(mgmVoucher.discount_amount || 0));
    const { error: useMgmError } = await db.from("mgm_treatment_vouchers")
      .update({ used: true, used_at: new Date().toISOString(), transaction_id: transactionId, updated_at: new Date().toISOString() })
      .eq("id", mgmVoucher.id)
      .eq("used", false);
    if (useMgmError) throw useMgmError;
    if (appliedDiscount > 0 && transactionId) {
      const { data: tx } = await db.from("transactions").select("gross_amount").eq("id", transactionId).single();
      if (tx) {
        await db.from("transactions")
          .update({ discount_amount: appliedDiscount, net_amount: Math.max(0, Number(tx.gross_amount) - appliedDiscount) })
          .eq("id", transactionId);
      }
    }
    return true;
  }

  // Cek ulang sebelum redeem
  const { data: row } = await db
    .from("survey_tokens")
    .select("id,voucher_used,prize_type")
    .eq("voucher_code", normalizedCode)
    .eq("clinic_id", CLINIC_ID)
    .maybeSingle();
  if (!row) throw new Error("Voucher tidak ditemukan.");
  if (row.voucher_used) throw new Error("Voucher sudah digunakan.");

  // Mark as used
  const { error: useErr } = await db.from("survey_tokens")
    .update({ voucher_used: true, voucher_used_at: new Date().toISOString() })
    .eq("voucher_code", normalizedCode)
    .eq("clinic_id", CLINIC_ID);
  if (useErr) throw useErr;

  // Jika DISCOUNT: update transaction.discount_amount + net_amount
  if (discountAmount > 0 && transactionId) {
    const { data: tx } = await db.from("transactions")
      .select("gross_amount")
      .eq("id", transactionId)
      .single();
    if (tx) {
      const newNet = Math.max(0, Number(tx.gross_amount) - discountAmount);
      await db.from("transactions")
        .update({ discount_amount: discountAmount, net_amount: newNet })
        .eq("id", transactionId);
    }
  }
  return true;
}

export async function saveClinicSettings({ name, legal_name, timezone, contact }) {
  const db = ensureClient();
  const result = await db.rpc("update_clinic_settings", {
    p_code:       CLINIC_ID,
    p_name:       name       || "",
    p_legal_name: legal_name || "",
    p_timezone:   timezone   || "Asia/Jakarta",
    p_contact:    contact    || {},
  });
  return assertResult(result);
}

export async function saveSatusehatOrganizationMapping({ organization_id, organization_name }) {
  const result = await ensureClient().rpc("save_satusehat_organization_mapping", {
    p_clinic_id: CLINIC_ID,
    p_organization_id: organization_id?.trim() || null,
    p_organization_name: organization_name?.trim() || null,
  });
  return assertResult(result) || {};
}

// ── Clinic Modules ─────────────────────────────────────────────────────────
export const ALL_MODULES = [
  { id: 'wellness',    name: 'Wellness / TCM',  icon: '🌿', division: 'WELLNESS',    desc: 'Akupunktur, herbal, pijat TCM, terapi tradisional' },
  { id: 'estetik',    name: 'Estetik',          icon: '✨', division: 'ESTETIK',     desc: 'Perawatan kecantikan, laser, filler, botox' },
  { id: 'dental',     name: 'Dental',           icon: '🦷', division: 'DENTAL',      desc: 'Dokter gigi, ortodonti, scaling, implant' },
  { id: 'dokter_umum',name: 'Dokter Umum',      icon: '🩺', division: 'DOKTER_UMUM', desc: 'Konsultasi umum, MCU, vaksinasi' },
  { id: 'fisioterapi',name: 'Fisioterapi',      icon: '♿', division: 'FISIOTERAPI', desc: 'Rehabilitasi, terapi fisik, latihan fungsional' },
  { id: 'apotik',     name: 'Apotik',           icon: '💊', division: 'APOTIK',      desc: 'Penjualan obat, resep dokter, farmasi' },
];

export async function loadClinicModules() {
  const db = ensureClient();
  const { data, error } = await db
    .from('clinic_modules')
    .select('module_id,is_active,activated_at')
    .eq('clinic_id', CLINIC_ID);
  if (error) throw error;
  // Map ke object { moduleId: isActive }
  const map = {};
  (data || []).forEach(r => { map[r.module_id] = r.is_active; });
  return map;
}

export async function saveClinicModule(moduleId, isActive) {
  const db = ensureClient();
  const { error } = await db
    .from('clinic_modules')
    .upsert({
      clinic_id:    CLINIC_ID,
      module_id:    moduleId,
      is_active:    isActive,
      activated_at: isActive ? new Date().toISOString() : null,
    }, { onConflict: 'clinic_id,module_id' });
  if (error) throw error;
}

// Helper: hasilkan array DIVISIONS berdasarkan modul yang aktif
export function getActiveDivisions(activeModules) {
  return ALL_MODULES
    .filter(m => activeModules[m.id])
    .map(m => ({ value: m.division, label: m.name }));
}

// ============================================================
// SISTEM CICILAN
// ============================================================

/**
 * Buat rencana cicilan baru untuk sebuah transaksi.
 * Cicilan ke-1 (DP) langsung PAID, paket langsung aktif.
 *
 * @param {string} transactionId
 * @param {Array}  installments  [{installment_number, due_date, amount}]
 * @param {Object} firstPayment  {payment_method, payment_reference, payment_notes}
 */
export async function createInstallmentPlan(transactionId, installments, firstPayment) {
  const db = ensureClient();
  const { data, error } = await db.rpc('create_installment_plan', {
    p_clinic_id:       CLINIC_ID,
    p_transaction_id:  transactionId,
    p_installments:    installments,
    p_first_payment:   firstPayment,
  });
  if (error) throw error;
  return data;
}

/**
 * Bayar satu cicilan (ke-2, ke-3, dst).
 * Jika semua cicilan lunas → transaksi otomatis PAID.
 *
 * @param {string} installmentId
 * @param {Object} payment  {payment_method, payment_reference, payment_notes}
 */
export async function payInstallment(installmentId, payment) {
  const db = ensureClient();
  const { data, error } = await db.rpc('pay_installment', {
    p_clinic_id:       CLINIC_ID,
    p_installment_id:  installmentId,
    p_payment:         payment,
  });
  if (error) throw error;
  return data;
}

/**
 * Muat semua cicilan PENDING/OVERDUE untuk halaman Piutang.
 * Menggunakan view v_pending_installments.
 */
export async function loadPendingInstallments() {
  const db = ensureClient();
  const { data, error } = await db
    .from('v_pending_installments')
    .select('*')
    .eq('clinic_id', CLINIC_ID)
    .order('due_date', { ascending: true });
  if (error) throw error;
  return data || [];
}

/**
 * Muat semua cicilan untuk satu transaksi (untuk banner PaymentForm).
 *
 * @param {string} transactionId
 */
export async function loadInstallmentsByTransaction(transactionId) {
  const db = ensureClient();
  const { data, error } = await db
    .from('transaction_installments')
    .select('*')
    .eq('transaction_id', transactionId)
    .eq('clinic_id', CLINIC_ID)
    .order('installment_number', { ascending: true });
  if (error) throw error;
  return data || [];
}

/**
 * Tandai cicilan-cicilan yang sudah lewat jatuh tempo sebagai OVERDUE.
 * Panggil saat halaman Piutang dibuka.
 */
export async function markOverdueInstallments() {
  const db = ensureClient();
  const { data, error } = await db.rpc('mark_overdue_installments', {
    p_clinic_id: CLINIC_ID,
  });
  if (error) throw error;
  return data; // jumlah row yang diupdate
}

// ─── Master Paket CRUD ──────────────────────────────────────────

export async function loadMasterPackages() {
  const db = ensureClient();
  const { data, error } = await db
    .from('package_masters')
    .select('*')
    .eq('clinic_id', CLINIC_ID)
    .order('package_name');
  if (error) throw error;
  return data || [];
}

export async function loadPackageItems(packageId) {
  const db = ensureClient();
  const { data, error } = await db
    .from('package_master_items')
    .select('id, service_id, quota, services(name, division, price)')
    .eq('package_id', packageId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function loadActiveServices() {
  const db = ensureClient();
  const { data, error } = await db
    .from('services')
    .select('id, name, division, price, status')
    .eq('clinic_id', CLINIC_ID)
    .eq('status', 'ACTIVE')
    .order('name');
  if (error) throw error;
  return data || [];
}

export async function saveMasterPackage(pkg, items) {
  const db = ensureClient();
  const isNew = !pkg.id;
  let packageId = pkg.id;
  const normalizedItems = items.map((item) => ({
    service_id: item.service_id,
    quota: Number(item.quota) || 1,
  }));
  const totalSessions = normalizedItems.reduce((sum, item) => sum + item.quota, 0);
  const includedServiceIds = normalizedItems.map((item) => item.service_id);

  if (isNew) {
    const { data, error } = await db
      .from('package_masters')
      .insert({
        clinic_id: CLINIC_ID,
        id: `PKG-${crypto.randomUUID().replaceAll('-', '').slice(0, 12).toUpperCase()}`,
        package_name: pkg.package_name,
        division: pkg.division || 'ALL',
        package_price: Number(pkg.package_price) || 0,
        valid_days: Number(pkg.valid_days) || 0,
        total_sessions: totalSessions,
        included_service_ids: includedServiceIds,
        status: pkg.status || 'ACTIVE',
      })
      .select('id')
      .single();
    if (error) throw error;
    packageId = data.id;
  } else {
    const { error } = await db
      .from('package_masters')
      .update({
        package_name: pkg.package_name,
        division: pkg.division || 'ALL',
        package_price: Number(pkg.package_price) || 0,
        valid_days: Number(pkg.valid_days) || 0,
        total_sessions: totalSessions,
        included_service_ids: includedServiceIds,
        status: pkg.status || 'ACTIVE',
      })
      .eq('id', packageId);
    if (error) throw error;
  }

  // Sync items: delete all existing then insert current
  await db.from('package_master_items').delete().eq('clinic_id', CLINIC_ID).eq('package_id', packageId);
  if (items.length > 0) {
    const rows = normalizedItems.map((item) => ({
      clinic_id: CLINIC_ID,
      package_id: packageId,
      service_id: item.service_id,
      quota: Number(item.quota) || 1,
    }));
    const { error } = await db.from('package_master_items').insert(rows);
    if (error) throw error;
  }

  return { id: packageId };
}

export async function deleteMasterPackage(id) {
  const db = ensureClient();
  const { error } = await db.from('package_masters').delete().eq('clinic_id', CLINIC_ID).eq('id', id);
  if (error) throw error;
}
